import { STORES, getAll, clearStore, remove, put } from './indexeddb.js';

let syncingOrders = false;
let syncingCustomers = false;

export async function syncPendingOrders() {
    if (syncingOrders || !navigator.onLine) return { synced: 0, failed: 0 };
    syncingOrders = true;

    try {
        const pending = await getAll(STORES.pendingOrders);
        if (pending.length === 0) return { synced: 0, failed: 0 };

        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

        const response = await fetch('/api/pos/sync-orders', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                ...(csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {}),
            },
            body: JSON.stringify({ orders: pending }),
        });

        if (!response.ok) throw new Error('Sync request failed');

        const data = await response.json();
        let synced = 0;
        let failed = 0;

        for (const result of data.results || []) {
            if (result.status === 'synced' && result.offline_id) {
                // Update customer mapping if order had a new customer
                if (result.offline_customer_id && result.server_customer_id) {
                    const customerPending = await getAll(STORES.pendingCustomers);
                    const customer = customerPending.find(
                        c => c.offline_id === result.offline_customer_id
                    );
                    if (customer) {
                        await remove(STORES.pendingCustomers, result.offline_customer_id);
                        // Update the local customers store with the server ID
                        const updatedCustomer = {
                            ...customer,
                            id: result.server_customer_id,
                            original_offline_id: result.offline_customer_id
                        };
                        await put(STORES.customers, updatedCustomer);
                    }
                }
                
                await remove(STORES.pendingOrders, result.offline_id);
                
                synced++;
            } else {
                failed++;
            }
        }

        return { synced, failed };
    } catch (e) {
        console.error('[Sync] syncPendingOrders error:', e);
        return { synced: 0, failed: 0 };
    } finally {
        syncingOrders = false;
    }
}

export async function syncPendingCustomers() {
    if (syncingCustomers || !navigator.onLine) return { synced: 0, failed: 0 };
    syncingCustomers = true;

    try {
        const pending = await getAll(STORES.pendingCustomers);
        if (pending.length === 0) return { synced: 0, failed: 0 };

        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

        const response = await fetch('/api/pos/sync-customers', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                ...(csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {}),
            },
            body: JSON.stringify({ customers: pending }),
        });

        if (!response.ok) throw new Error('Sync customers request failed');

        const data = await response.json();
        let synced = 0;
        let failed = 0;

        for (const result of data.results || []) {
            if (result.status === 'synced' && result.offline_id) {
                // Remove from pending
                await remove(STORES.pendingCustomers, result.offline_id);
                
                // Update local customers store with server ID
                const customerData = pending.find(c => c.offline_id === result.offline_id);
                if (customerData) {
                    await put(STORES.customers, {
                        ...customerData,
                        id: result.server_id,
                        original_offline_id: result.offline_id
                    });
                }
                
                synced++;
            } else {
                failed++;
            }
        }

        return { synced, failed };
    } catch (e) {
        return { synced: 0, failed: 0 };
    } finally {
        syncingCustomers = false;
    }
}

export function startAutoSync(intervalMs = 30000) {
    syncPendingCustomers();
    syncPendingOrders();

    setInterval(() => {
        if (navigator.onLine) {
            syncPendingCustomers();
            syncPendingOrders();
        }
    }, intervalMs);

    window.addEventListener('online', () => {
        syncPendingCustomers();
        syncPendingOrders();
    });
}